import React, { useId } from 'react';
import { Button, Card, Popper, Text } from '@fragments';
import { IconArrowUpRight, IconInfinity, IconTrendingUp } from '@tabler/icons-react';
import TopicScore from '../../../TopicScore';
import { OfficialSource, SourceChips } from '../../../SourceCitation';
import useHoverPanel from '../../../../hooks/useHoverPanel';
import './styles.scss';

// A trending topic under the prompt, as a short version of the gallery's card: its
// classification and relevance on top, the topic in up to two lines. The whole card picks
// it into the prompt, as a chip (TopicToken), and the topics step aside until it is
// removed; hovering or focusing it opens the full card (why it fits, sources, the trend signal).
export default function TopicPreview({ topic, studio: s }) {
  const { t } = s;
  const id = useId();
  const { control, trigger, panel, primary: choose, open, setOpen, close, openPanel, hoverIn, hoverOut, onPanelKeyDown, onTriggerKeyDown } = useHoverPanel();
  const eligible = topic.eligible_for_generation;
  const trending = topic.classification === 'trending';
  const select = () => {
    if (!eligible) return;
    s.chooseTopic(topic);
    close();
    document.querySelector('.studio-composer textarea')?.focus({ preventScroll: true });
  };

  const show = () => openPanel(
    <Card ref={panel} id={id} role="dialog" aria-labelledby={`${id}-title`} className="studio-topic-preview" bare noBodyPadding
      onKeyDown={onPanelKeyDown}
      footer={<Button ref={choose} fullWidth size="small" color="inverse" variant="rounded" disabled={!eligible} endIcon={<IconArrowUpRight />} onClick={select}>{t('chooseTopic')}</Button>}>
      <div className="studio-topic-preview__meta"><Text.caption contrast={6}>{t(trending ? 'trending' : 'evergreen')}</Text.caption><TopicScore topic={topic} t={t} /></div>
      <Text.label id={`${id}-title`} contrast={9}>{topic.title}</Text.label>
      <Text.p contrast={7}>{topic.relevance_reason} <SourceChips evidence={topic.evidence} t={t} /></Text.p>
      {topic.trend_evidence && <Text.caption contrast={6}>{topic.trend_evidence}</Text.caption>}
      <OfficialSource evidence={topic.evidence} t={t} />
      {!eligible && <Text.caption contrast={7}>{t('topicUnavailable')}</Text.caption>}
    </Card>);

  return <Popper controlRef={control} config={{ activateOnHover: true }} onOpenChange={setOpen} paperSx={{
    width: 'min(320px, calc(100vw - 32px))', maxWidth: 'none',
    '& .popper-header': { padding: 0, border: 0 },
    // This popper contains a custom card and CTA, rather than an options list.
    '& > .MuiList-root': { display: 'none' },
  }}>
    <button ref={trigger} type="button" className={`studio-topic-mini${eligible ? '' : ' is-unavailable'}`}
      aria-labelledby={`${id}-name`} aria-describedby={`${id}-kind`}
      aria-expanded={open} aria-haspopup="dialog" aria-controls={open ? id : undefined}
      aria-disabled={!eligible}
      onMouseEnter={() => hoverIn(show)}
      onMouseLeave={hoverOut} onFocus={show} onClick={event => { event.stopPropagation(); if (eligible) select(); else show(); }}
      onKeyDown={onTriggerKeyDown}>
      <span className="studio-topic-mini__meta">
        <Text.caption id={`${id}-kind`} contrast={6} left={trending ? <IconTrendingUp /> : <IconInfinity />}>{t(trending ? 'trending' : 'evergreen')}</Text.caption>
        <TopicScore topic={topic} t={t} />
      </span>
      <Text.label id={`${id}-name`} className="studio-topic-mini__title" contrast={eligible ? 9 : 6}>{topic.title}</Text.label>
    </button>
  </Popper>;
}
